import type { UIMessage } from "ai";

/**
 * Per-message timestamp for the Sphere AI chat (MDOT-24461).
 *
 * AI SDK v6 `UIMessage` has no built-in `createdAt`, so we mint one client-side and
 * carry it in the message metadata. It is nested under `custom` on purpose: the
 * `@assistant-ui/react-ai-sdk` message converter maps an AI SDK message's
 * `metadata.custom` onto the rendered `MessageState.metadata.custom` (any other
 * metadata key is dropped by `fromThreadMessageLike`), so `custom` is the only path
 * that survives to the renderer. The value is epoch milliseconds (`Date.now()`).
 *
 * CROSS-REPO CONTRACT, kept in sync by hand: since MDOT-24442 the ASSISTANT message's
 * timestamps are minted server-side by the `messageMetadata` callback in
 * sphere-mcp/src/server/chat/route.ts, which stamps `createdAt` on the stream's
 * `start` and `finishedAt` on its `finish`, and carries `turnStartedAt` and the turn's
 * running `turnTokens` forward from the previous leg of the same turn. This type is the only description of that
 * payload's shape; user messages are stamped client-side, as they are sent
 * (see `withSendTimestamp`), and carry `createdAt` alone.
 */
export type SphereAIMessageMetadata = {
	custom?: {
		/** Epoch milliseconds the message was created, minted client-side. */
		createdAt?: number;
		/**
		 * Epoch milliseconds the assistant's stream ENDED, minted server-side on the
		 * `finish` part. Assistant messages only: `createdAt` is stamped when the stream
		 * opens, so the pair is what gives a stored transcript a per-message duration.
		 */
		finishedAt?: number;
		/**
		 * Epoch milliseconds the user's question was accepted, minted server-side and
		 * copied onto every assistant message of the same turn. Assistant messages only.
		 *
		 * A turn is several requests — the agent loop runs here, so a question answered
		 * after three tool calls is four POSTs — and this is what marks them as one turn:
		 * messages sharing a `turnStartedAt` are one answer, and `finishedAt` of the last
		 * of them minus this is what the user waited.
		 */
		turnStartedAt?: number;
		/**
		 * Which leg of the turn produced this message, 1-based, minted server-side.
		 * Assistant messages only.
		 *
		 * A turn's last leg carries the highest number, so it is also how many model turns
		 * the question took — the axis that separates "one slow tool call" from "the model
		 * explored for eight legs", which a duration alone cannot.
		 */
		turnLeg?: number;
		/**
		 * What the turn has billed through this leg, in the four quantities Anthropic prices
		 * separately, minted server-side and accumulated across the turn's legs. Assistant
		 * messages only.
		 *
		 * Read by nothing in the browser. It is here because the leg that produces it is one
		 * request and the leg that adds to it is the next, landing on another replica, so the
		 * running total has nowhere to live but this message — and because a stored transcript
		 * carrying its own turn totals is what makes a conversation's spend answerable at all.
		 * The counts are disjoint, so the four add up to the turn's whole cost.
		 */
		turnTokens?: {
			inputUncached?: number;
			cacheRead?: number;
			cacheWrite?: number;
			output?: number;
		};
		/**
		 * Present only when sphere-mcp's own loop guard STOPPED this turn's agent loop.
		 * Assistant messages only, and absent on every healthy turn.
		 *
		 * The agent loop runs server-side now, so a run it stops arrives here as one
		 * assistant message whose tool calls are all resolved — which is exactly the shape
		 * `sendAutomaticallyWhen` resumes on. Without this key the browser would restart
		 * the very run the server just stopped, and the guard would read as doing nothing.
		 * See loopGuard.ts's `serverLoopStop`, which turns it into the same `LoopStop` the
		 * client's own detection produces.
		 *
		 * The FACT, not a sentence: the wording of the notice lives in loopGuard.ts and
		 * nowhere else, so the two repositories cannot drift on what the user reads.
		 */
		loopStop?: {
			/** The tool that was called over and over. */
			toolName?: string;
			/** How many times, with identical arguments. */
			count?: number;
			/** `max-turns`: the run hit the server's step ceiling, and this answer is partial. */
			reason?: "max-turns";
			/** The ceiling it hit, with `reason: "max-turns"`. */
			steps?: number;
		};
	};
};

/** A Sphere AI chat message: an AI SDK `UIMessage` carrying our timestamp metadata. */
export type SphereUIMessage = UIMessage<SphereAIMessageMetadata>;

/**
 * Parse an ISO date string to epoch milliseconds, or `undefined` when absent/invalid.
 * Used to anchor best-effort timestamps for legacy conversations to their stored
 * conversation-level time (lastMessageTime/creationTime).
 */
export function parseIsoToMs(iso: string | undefined): number | undefined {
	if (!iso) {
		return undefined;
	}
	const ms = Date.parse(iso);
	return Number.isNaN(ms) ? undefined : ms;
}

/**
 * The single timestamp anchor for a stored conversation, used to backfill per-message
 * `createdAt` for messages that never carried one. The conversation's own activity time
 * when it has one, else its creation time, else the wall clock. Restore and poll MUST use
 * the same expression (computed once per conversation and reused) so an un-stamped message
 * is not backfilled to a different value on every poll — which would make the "did anything
 * change?" check fire on client-invented data. See SphereAIChatSection's poll effect.
 */
export function conversationAnchorMs(lastMessageTime: string | undefined, creationTime: string | undefined): number {
	return parseIsoToMs(lastMessageTime) ?? parseIsoToMs(creationTime) ?? Date.now();
}

/** Read the minted `createdAt` (epoch ms) from a message, or `undefined` when absent. */
export function getMessageCreatedAt(message: UIMessage): number | undefined {
	const metadata = message.metadata as SphereAIMessageMetadata | undefined;
	return metadata?.custom?.createdAt;
}

/**
 * Read our metadata bag off any message-shaped value. The two callers below are constrained to
 * `object` rather than to a `{ metadata }` shape on purpose: an outgoing message legitimately has
 * no `metadata` key at all (see `withSendTimestamp`), and TypeScript's weak-type check rejects such
 * a value against a type whose every property is optional.
 */
function readMetadata(message: object): SphereAIMessageMetadata | undefined {
	return (message as { metadata?: SphereAIMessageMetadata }).metadata;
}

/**
 * Return a copy of `message` with `metadata.custom.createdAt` set to `createdAt`.
 *
 * Structurally typed rather than `UIMessage`-constrained so it also accepts the `CreateUIMessage`
 * handed to `sendMessage` — a message on its way out has no `id`/`role` yet, but it carries (or
 * gains) the same metadata bag.
 */
function withCreatedAt<T extends object>(message: T, createdAt: number): T {
	const metadata = readMetadata(message) ?? {};
	return {
		...message,
		metadata: { ...metadata, custom: { ...metadata.custom, createdAt } },
	};
}

/**
 * Stamp a message on its way OUT of the composer, so the user's own turn carries its real send
 * time from the first frame it renders.
 *
 * `ensureMessageTimestamps` backfills before persisting, which is enough for the stored record but
 * too late for the live transcript: until the turn finishes, the user message is the only one
 * without a time (the assistant's is minted server-side), and every renderer keyed on `createdAt`
 * skips it. So the day separator treats the assistant's reply as the message that OPENS the day and
 * lands between the question and the answer, and the user bubble shows no time on hover. Both
 * correct themselves on reload — which is exactly the inconsistency this removes.
 *
 * An existing stamp is never overwritten (a re-sent message keeps the time it was first sent), and
 * the clock is injected for the same reason as in `ensureMessageTimestamps`.
 */
export function withSendTimestamp<T extends object>(message: T, now: number): T {
	return typeof readMetadata(message)?.custom?.createdAt === "number" ? message : withCreatedAt(message, now);
}

/**
 * Ensure every message carries a `metadata.custom.createdAt`, without mutating input.
 *
 * Pure and side-effect free: the current time is injected as `now` (epoch ms) so the
 * function stays deterministic and testable (mirrors the `nowMs` clock-injection
 * pattern in sphere-mcp's history-store). Messages that already have a timestamp are left
 * untouched. Missing ones are backfilled so the assigned times are monotonically
 * non-decreasing with message order and the newest missing message is stamped with
 * `now`: we walk from the end, and each earlier un-stamped message is stamped one
 * millisecond before the timestamp that follows it. This is a best-effort ordering,
 * not a record of the real send time (which was never captured for these messages).
 */
export function ensureMessageTimestamps<T extends UIMessage>(messages: T[], now: number): T[] {
	// `next` tracks the timestamp of the message after the current one, so an
	// un-stamped message can be placed strictly before it (monotonic ordering).
	// Seeded with `now`, so the last message (if un-stamped) gets exactly `now`.
	let next = now;
	// Build back-to-front, then reverse, so each stamp can reference its successor.
	const reversed: T[] = [];
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		const existing = getMessageCreatedAt(message);
		if (existing !== undefined) {
			reversed.push(message);
			// Keep later backfilled stamps strictly before a known earlier timestamp.
			next = Math.min(next, existing);
			continue;
		}
		const createdAt = next;
		reversed.push(withCreatedAt(message, createdAt));
		// The next (earlier) un-stamped message must precede this one.
		next = createdAt - 1;
	}
	reversed.reverse();
	return reversed;
}
